import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Search, Trash2, Users, UsersRound, X } from 'lucide-react';
import { useState } from 'react';
import { useDebounced } from '../components/record-table/use-debounced.ts';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { Input } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { describeError } from '../lib/api.ts';
import { api, type MemberView, type TeamView } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';

/** The name filter waits for typing to pause, and ignores a single character. */
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;

/**
 * Admin → Teams (TS-1861): the organisation's teams, who is in each and who leads it. A person
 * may be in several teams and a team may have several leads. Everyone can look; changing needs
 * `team.manage`. Teams do not change what anyone can see yet (the `team` scope comes later).
 */
export function TeamsPage() {
  const session = useSession();
  const qc = useQueryClient();
  const org = session.me.organisation.id;
  const canManage = session.can('team.manage');
  const teams = useQuery({ queryKey: ['teams', org], queryFn: api.teams.list });
  const members = useQuery({
    queryKey: ['members', org],
    queryFn: api.users,
    enabled: canManage && session.can('user.read'),
  });
  const [naming, setNaming] = useState<TeamView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<TeamView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const settled = useDebounced(query.trim().toLowerCase(), SEARCH_DEBOUNCE_MS);

  // Every change is one call that answers with the team; the list is re-read after it. A
  // dialog closes itself through its own call's onSuccess, so a card change landing while a
  // dialog is open never closes it.
  const write = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['teams', org] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const dialogOpen = naming !== null || deleting !== null;
  // The error belongs to whatever is open: a dialog shows its own, so start it clean.
  const openNaming = (t: TeamView | 'new') => {
    setError(null);
    setNaming(t);
  };
  const openDeleting = (t: TeamView) => {
    setError(null);
    setDeleting(t);
  };
  const closeDialogs = () => {
    setError(null);
    setNaming(null);
    setDeleting(null);
  };

  const all = teams.data ?? [];
  // Filters run in the browser: the whole list is already loaded.
  const inTeams = [
    ...new Map(all.flatMap((t) => t.members).map((m) => [m.userId, m] as const)).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));
  // Someone who left every team is no longer an option, so they drop out of the filter.
  const picked = memberIds.filter((id) => inTeams.some((m) => m.userId === id));
  const q = settled.length >= SEARCH_MIN_CHARS ? settled : '';
  // Several people: the teams any of them is in.
  const shown = all.filter(
    (t) =>
      (!q || t.name.toLowerCase().includes(q)) &&
      (picked.length === 0 || t.members.some((m) => picked.includes(m.userId))),
  );

  const newTeam = canManage && (
    <Button onClick={() => openNaming('new')} data-testid="new-team">
      <Plus /> New team
    </Button>
  );

  return (
    <Gate permission="team.read">
      <PageHeader
        title="Teams"
        description="Who works together, and who leads each team."
        actions={newTeam}
      />
      {error && !dialogOpen && (
        <p
          className="bg-state-danger-bg text-state-danger-fg border-state-danger-border mb-5 rounded-xl border px-4 py-3 text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
      {teams.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(teams.error)}
        </p>
      )}
      {teams.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {!teams.data ? null : teams.data.length === 0 ? (
        <EmptyState
          icon={UsersRound}
          title="No teams yet."
          description="Group people who work together and mark who leads them."
          action={newTeam}
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative max-w-sm min-w-48 flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" />
              <Input
                aria-label="Filter teams by name"
                placeholder="Filter by team name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label="Filter teams by member">
                  <Users />
                  {picked.length === 0
                    ? 'Any member'
                    : picked.length === 1
                      ? inTeams.find((m) => m.userId === picked[0])?.name
                      : `${picked.length} members`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                <DropdownMenuItem onSelect={() => setMemberIds([])}>Any member</DropdownMenuItem>
                <DropdownMenuSeparator />
                {inTeams.map((m) => (
                  <DropdownMenuCheckboxItem
                    key={m.userId}
                    checked={picked.includes(m.userId)}
                    // Keep the menu open: picking several people is the point.
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(on) =>
                      setMemberIds(
                        on ? [...picked, m.userId] : picked.filter((id) => id !== m.userId),
                      )
                    }
                  >
                    {m.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="text-muted-foreground text-sm">
              {shown.length} of {all.length} team{all.length === 1 ? '' : 's'}
            </span>
          </div>
          {shown.length === 0 && (
            <EmptyState icon={UsersRound} title="No team matches that filter." />
          )}
          <div className="grid gap-5 lg:grid-cols-2">
            {shown.map((team) => (
              <TeamCard
                key={team.id}
                team={team}
                people={members.data ?? []}
                canManage={canManage}
                busy={write.isPending}
                onWrite={(fn) => write.mutate(fn)}
                onRename={() => openNaming(team)}
                onDelete={() => openDeleting(team)}
              />
            ))}
          </div>
        </>
      )}

      <NameDialog
        key={naming === 'new' ? 'new' : (naming?.id ?? 'closed')}
        team={naming}
        people={(members.data ?? []).filter((p) => p.status !== 'deactivated')}
        busy={write.isPending}
        error={error}
        onClose={closeDialogs}
        onSave={(name, picked) =>
          write.mutate(
            () =>
              naming === 'new'
                ? api.teams.create({ name, members: picked })
                : api.teams.rename(naming!.id, name),
            { onSuccess: () => setNaming(null) },
          )
        }
      />

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && closeDialogs()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              Its members leave the team. Nobody loses access to anything.
            </DialogDescription>
          </DialogHeader>
          {error && <DialogError>{error}</DialogError>}
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialogs}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleting &&
                write.mutate(() => api.teams.remove(deleting.id), {
                  onSuccess: () => setDeleting(null),
                })
              }
              disabled={write.isPending}
              data-testid="confirm-delete-team"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Gate>
  );
}

function TeamCard({
  team,
  people,
  canManage,
  busy,
  onWrite,
  onRename,
  onDelete,
}: {
  team: TeamView;
  people: MemberView[];
  canManage: boolean;
  busy: boolean;
  onWrite: (fn: () => Promise<unknown>) => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <Card data-testid="team">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="truncate">{team.name}</CardTitle>
          <CardDescription>
            {team.members.length} member{team.members.length === 1 ? '' : 's'}
          </CardDescription>
        </div>
        {canManage && (
          <div className="flex shrink-0 gap-1">
            <Button size="sm" variant="ghost" onClick={onRename} aria-label={`Rename ${team.name}`}>
              <Pencil />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:text-danger"
              onClick={onDelete}
              aria-label={`Delete ${team.name}`}
            >
              <Trash2 />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <MembersEditor
          teamName={team.name}
          members={team.members}
          people={people}
          canManage={canManage}
          busy={busy}
          onAdd={(userId) => onWrite(() => api.teams.addMember(team.id, userId))}
          onToggleLead={(m) => onWrite(() => api.teams.setLead(team.id, m.userId, !m.isLead))}
          onRemove={(m) => onWrite(() => api.teams.removeMember(team.id, m.userId))}
        />
      </CardContent>
    </Card>
  );
}

type Member = TeamView['members'][number];

/**
 * A team's members with their Lead box and remove button, and the "Add a member…" picker. The
 * team card and the New team dialog draw the same one: the card saves each change at once, the
 * dialog keeps them until Save.
 */
function MembersEditor({
  teamName,
  members,
  people,
  canManage,
  busy,
  onAdd,
  onToggleLead,
  onRemove,
}: {
  teamName: string;
  members: Member[];
  people: MemberView[];
  canManage: boolean;
  busy: boolean;
  onAdd: (userId: string) => void;
  onToggleLead: (m: Member) => void;
  onRemove: (m: Member) => void;
}) {
  // Bumped on every pick, so the picker is back to its placeholder after each attempt, failed
  // ones included (picking the same person again would not fire onValueChange otherwise).
  const [picks, setPicks] = useState(0);
  const inTeam = new Set(members.map((m) => m.userId));
  const addable = people.filter((p) => !inTeam.has(p.id) && p.status !== 'deactivated');
  return (
    <div className="flex flex-col gap-3">
      {members.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nobody in this team yet.</p>
      ) : (
        // About 4.4 rows (56px each): a big team scrolls inside its card, and the cut-off row
        // shows there is more.
        <ul className="divide-border max-h-[15.5rem] divide-y overflow-y-auto">
          {members.map((m) => (
            <li key={m.userId} className="flex items-center gap-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{m.name}</span>
                <span className="text-muted-foreground block truncate text-xs">{m.email}</span>
              </span>
              {canManage ? (
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={m.isLead}
                    disabled={busy}
                    onChange={() => onToggleLead(m)}
                    aria-label={`${m.email} leads ${teamName}`}
                  />
                  Lead
                </label>
              ) : (
                m.isLead && <Badge variant="outline">Lead</Badge>
              )}
              {canManage && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => onRemove(m)}
                  aria-label={`Remove ${m.email} from ${teamName}`}
                >
                  <X />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && addable.length > 0 && (
        <Select
          key={picks}
          disabled={busy}
          onValueChange={(userId) => {
            setPicks((n) => n + 1);
            onAdd(userId);
          }}
        >
          <SelectTrigger className="h-9" aria-label={`Add a member to ${teamName}`}>
            <SelectValue placeholder="Add a member…" />
          </SelectTrigger>
          <SelectContent>
            {addable.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/** Create (name and first members, one call) or rename (name only) a team. */
function NameDialog({
  team,
  people,
  busy,
  error,
  onClose,
  onSave,
}: {
  team: TeamView | 'new' | null;
  /** Who may join: current members of the organisation. */
  people: MemberView[];
  busy: boolean;
  /** The last save's refusal (a taken name, a member who left), shown in the dialog. */
  error: string | null;
  onClose: () => void;
  onSave: (name: string, members: Array<{ userId: string; isLead: boolean }>) => void;
}) {
  // Keyed on the team by the caller, so this starts from the right name each time it opens.
  const [name, setName] = useState(team && team !== 'new' ? team.name : '');
  const [picked, setPicked] = useState<Member[]>([]);
  return (
    <Dialog open={team !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim())
              onSave(
                name.trim(),
                picked.map((m) => ({ userId: m.userId, isLead: m.isLead })),
              );
          }}
        >
          <DialogHeader>
            <DialogTitle>{team === 'new' ? 'New team' : 'Rename team'}</DialogTitle>
          </DialogHeader>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Name
            <Input
              autoFocus
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Team name"
            />
          </label>
          {team === 'new' && people.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Members</span>
              <MembersEditor
                teamName={name.trim() || 'the new team'}
                members={picked}
                people={people}
                canManage
                busy={busy}
                onAdd={(userId) => {
                  const p = people.find((x) => x.id === userId)!;
                  setPicked((xs) => [
                    ...xs,
                    { userId, name: p.name, email: p.email, isLead: false },
                  ]);
                }}
                onToggleLead={(m) =>
                  setPicked((xs) =>
                    xs.map((x) => (x.userId === m.userId ? { ...x, isLead: !x.isLead } : x)),
                  )
                }
                onRemove={(m) => setPicked((xs) => xs.filter((x) => x.userId !== m.userId))}
              />
            </div>
          )}
          {error && <DialogError>{error}</DialogError>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || busy} data-testid="save-team">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DialogError({ children }: { children: string }) {
  return (
    <p className="text-danger text-sm" role="alert">
      {children}
    </p>
  );
}
